import 'swiper/css';

import { useIsClient } from '@actr/hooks/src/useIsClient';
import { IconChevronLeft } from '@actr/icons/chevron-left';
import { IconChevronRight } from '@actr/icons/chevron-right';
import { IBaseComponentProps } from '@actr/specs/src/base-component-props';
import { Sizes } from '@actr/style/src/specs/theme-v2';
import { Button, ButtonSizes, ButtonTypes } from '@actr/ui/src/components/_atoms/button';
import { FlexDirection, FlexJustify, FlexWrapper } from '@actr/ui/src/components/_atoms/flex-wrapper';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { formatMoney } from '@actr/utilities/src/currency';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper/types';

import DestinationCard from '@/components/page-constucts/destinations-block/destination-card';
import aviaMainPageYMLogger, { AviaMainPageYMLogs } from '@/logs/ym/avia/main-page';
import { PopularDestinationItem } from '@/services/models/avia/main-page-info';

import * as Styled from './style.css';

export interface DestinationsBlockProps extends IBaseComponentProps {
   data: PopularDestinationItem;
   buttonRoute?: string;
   buttonRouteText?: string;
}

function getMaxSlidesPerView() {
  if (window.innerWidth >= 1050) return 5;
  if (window.innerWidth >= 820) return 4;
  if (window.innerWidth >= 600) return 3;

  return 2;
}

const DestinationsBlock: FC<DestinationsBlockProps> = observer(({ data,
  buttonRoute,
  className,
  buttonRouteText }) => {
  const refSwiper = useRef<SwiperType | undefined>(undefined);
  const isClient = useIsClient();
  const { title, description, list } = data;
  const [maxSlidesPerView, setMaxSlidesPerView] = useState(5);
  const minPrice = Math.min(...list.map(destination => destination.min_price).filter(Boolean) as number[]);
  const getSlidesPerView = (capacity: number) => Math.max(1, Math.min(list.length, capacity));
  const baseSlidesPerView = getSlidesPerView(2);
  const tabletSlidesPerView = getSlidesPerView(3);
  const smallDesktopSlidesPerView = getSlidesPerView(4);
  const desktopSlidesPerView = getSlidesPerView(5);
  const getSlideWidth = (slidesPerView: number) => (
    `calc((100% - ${(slidesPerView - 1) * 16}px) / ${slidesPerView})`
  );
  // SSR использует те же ширины, которые Swiper рассчитает на клиенте для каждого breakpoint.
  const swiperStyle = {
    '--destination-slide-width-base': getSlideWidth(baseSlidesPerView),
    '--destination-slide-width-tablet': getSlideWidth(tabletSlidesPerView),
    '--destination-slide-width-small-desktop': getSlideWidth(smallDesktopSlidesPerView),
    '--destination-slide-width-desktop': getSlideWidth(desktopSlidesPerView),
  } as React.CSSProperties;
  const canLoop = isClient && list.length > maxSlidesPerView;

  useEffect(() => {
    if (!isClient) return undefined;

    const updateMaxSlidesPerView = () => setMaxSlidesPerView(getMaxSlidesPerView());

    updateMaxSlidesPerView();
    window.addEventListener('resize', updateMaxSlidesPerView);

    return () => window.removeEventListener('resize', updateMaxSlidesPerView);
  }, [isClient]);

  function handleClickShowMore() {
    aviaMainPageYMLogger.send(AviaMainPageYMLogs.SHOW_MORE_DESTINATIONS_BUTTON);
  }

  return (
    <ContentBlock>
      <FlexWrapper direction={FlexDirection.ROW} gap={Sizes.X2S} justify={FlexJustify.SPACE_BETWEEN}>
        <FlexWrapper direction={FlexDirection.COL} gap={Sizes.X4S}>
          <Text tagName="h3" font="title.x2l">{title}</Text>
          {!!description && (
            <Text font="paragraph.m" className={Styled.Description}>
              {description.replace(/{{min_price}}/g, formatMoney(minPrice))}
            </Text>
          )}
        </FlexWrapper>
        {canLoop && (
        <FlexWrapper direction={FlexDirection.ROW} gap={Sizes.X2S} className={Styled.SliderButtons}>
          <Button
            size={ButtonSizes.S}
            buttonType={ButtonTypes.TERTIARY}
            leftIcons={(<IconChevronLeft />)}
            className={Styled.SliderButton}
            onClick={() => refSwiper.current?.slidePrev()}
          />
          <Button
            size={ButtonSizes.S}
            buttonType={ButtonTypes.TERTIARY}
            leftIcons={(<IconChevronRight />)}
            className={Styled.SliderButton}
            onClick={() => refSwiper.current?.slideNext()}
          />
        </FlexWrapper>
        )}
      </FlexWrapper>
      <Space size={Sizes.M} />
      <div className={Styled.MobileWrapper}>
        <div className={Styled.MobileBar}>
          {list.map((item, index) => (
            <DestinationCard
              name={item.name}
              image={item.image}
              route={item.route}
              imageIndex={index}
              showSchema={false}
              key={index}
              fixedImgWidth="216px"
              minPrice={item.min_price}
            />
          ))}
        </div>
      </div>
      <FlexWrapper className={classNames(Styled.DestinationsCardsWrapper, className)} gap={Sizes.M}>
        <Swiper
          // Swiper 12 должен пересоздать loop при переходе между переполненным и статичным breakpoint.
          key={`${maxSlidesPerView}-${canLoop}`}
          className={Styled.Swiper}
          direction="horizontal"
          slidesPerView={baseSlidesPerView}
          breakpoints={{
            600: {
              slidesPerView: tabletSlidesPerView,
            },
            820: {
              slidesPerView: smallDesktopSlidesPerView,
            },
            1050: {
              slidesPerView: desktopSlidesPerView,
            }
          }}
          height={260}
          loop={canLoop}
          spaceBetween={16}
          roundLengths={true}
          style={swiperStyle}
          watchOverflow={true}
          onSwiper={swiper => {
            refSwiper.current = swiper;
          }}>
          {list.map((item, index) => (
            <SwiperSlide key={index} className={Styled.Slide}>
              <DestinationCard
                name={item.name}
                image={item.image}
                route={item.route}
                imageIndex={index}
                showSchema={false}
                fixedImgWidth="216px"
                minPrice={item.min_price}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </FlexWrapper>
      <Space size={Sizes.M} />
      { buttonRoute && (
      <a href={buttonRoute}>
        <Button
          onClick={handleClickShowMore}
          className={Styled.ShowMoreButton}
          size={ButtonSizes.M}
          buttonType={ButtonTypes.PRIMARY}>
          { buttonRouteText ?? 'Посмотреть ещё' }
        </Button>
      </a>
      )}
    </ContentBlock>
  );
});

export default DestinationsBlock;
